import { Tooltip } from '@alphonsocode/kallos';
import React from 'react';
import SecondRowButtons from './secondRowButtons';

const CARD_ROW_FONT_SIZE = 13;

const labelStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: CARD_ROW_FONT_SIZE,
  color: '#000000',
  flexShrink: 0,
  marginRight: 6,
  marginBottom: 0,
};

const valueBase: React.CSSProperties = {
  fontWeight: 400,
  fontSize: CARD_ROW_FONT_SIZE,
  color: '#000000',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  display: 'inline-block',
};

function FieldCell({
  label,
  children,
  flex = '0 0 22%',
  tooltip,
  variant = 'default',
  truncate = true,
  valueMaxWidth,
  multiline = false,
}: {
  label: string;
  children: React.ReactNode;
  flex?: string;
  tooltip?: string;
  variant?: 'default' | 'long';
  truncate?: boolean;
  valueMaxWidth?: string | number;
  multiline?: boolean;
}) {
  const isLong = variant === 'long';
  const tooltipValue =
    typeof tooltip === 'string'
      ? tooltip
      : typeof children === 'string'
        ? children
        : '';

  const inner = (
    <span
      style={
        isLong
          ? {
              marginLeft: 5,
              fontWeight: 400,
              fontSize: CARD_ROW_FONT_SIZE,
              color: '#000000',
              overflow: multiline ? 'visible' : 'hidden',
              textOverflow: multiline ? 'clip' : 'ellipsis',
              whiteSpace: multiline ? 'pre-line' : 'nowrap',
              maxWidth: valueMaxWidth ?? '320px',
              minWidth: 0,
              display: 'inline-block',
            }
          : {
              ...valueBase,
              flex: truncate ? 1 : '0 0 auto',
              maxWidth: valueMaxWidth,
              overflow: multiline ? 'visible' : truncate ? 'hidden' : 'visible',
              textOverflow: multiline ? 'clip' : truncate ? 'ellipsis' : 'clip',
              whiteSpace: multiline ? 'pre-line' : valueBase.whiteSpace,
            }
      }
    >
      {children}
    </span>
  );

  return (
    <div
      className="d-flex align-items-center"
      style={{
        flex: isLong ? '1 1 auto' : flex,
        minWidth: 0,
        maxWidth: '100%',
        fontFamily: 'lg-smart, sans-serif',
      }}
    >
      <label
        style={
          isLong
            ? {
                fontWeight: 'bold',
                fontSize: CARD_ROW_FONT_SIZE,
                marginLeft: 26,
                color: '#000000',
                flexShrink: 0,
                marginBottom: 0,
              }
            : labelStyle
        }
      >
        {label}
      </label>
      {tooltip ? (
        <Tooltip
          overlay={tooltipValue}
          side="bottom"
          align="start"
          sideOffset={4}
          openDelay={0}
          closeDelay={0}
          arrow={false}
          style={{
            maxWidth: '300px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {inner}
        </Tooltip>
      ) : (
        inner
      )}
    </div>
  );
}

export function SecondRow({
  data,
  mediaItems,
  testType,
  artifactsVisible = true,
  assetsLoaded = false,
  assetsLoading = false,
  ensureAssetsLoaded = async () => [],
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mediaItems: any[];
  testType: string;
  artifactsVisible?: boolean;
  assetsLoaded?: boolean;
  assetsLoading?: boolean;
  ensureAssetsLoaded?: () => Promise<any[]>;
}) {
  const isProgrammatic = testType === 'ssp';
  const destinationType = data.testMetadata?.destination_type;
  const clickThrough = data.testMetadata?.clickUrlDetails?.clickThrough || {};
  const clickAction = data.testMetadata?.click_action || {};
  const appIDFromDB = isProgrammatic
    ? clickThrough.appGroupId || clickAction.app_id
    : data.testMetadata.clickUrlDetails?.clickThrough?.appGroupId;
  const deeplinkParams = isProgrammatic
    ? clickThrough.deeplinkParams ||
      clickAction.deeplinkParams ||
      clickAction.deeplink_params
    : data.testMetadata.clickUrlDetails?.clickThrough?.deeplinkParams;
  const contentTargetFromDB = isProgrammatic
    ? clickThrough.contentTarget ||
      clickThrough.content_target ||
      clickAction.contentTarget ||
      clickAction.content_target ||
      deeplinkParams?.contentTarget ||
      deeplinkParams?.content_target
    : deeplinkParams?.contentTarget;
  const formattedDeeplinkParams =
    deeplinkParams && typeof deeplinkParams === 'object'
      ? Object.entries(deeplinkParams)
          .map(([key, value]) => `${key}:${String(value)}`)
          .join('\n')
      : '';

  const appId = appIDFromDB || '-';
  const contentTarget = contentTargetFromDB || formattedDeeplinkParams || '-';
  const isContentTargetMultiline =
    !contentTargetFromDB && Boolean(formattedDeeplinkParams);

  const destTypesToReplace = ['Image', 'Video', 'Stream', 'Web', 'WebFS'];
  const isReplacedType = destTypesToReplace.includes(destinationType);

  const destinationLabel = `${destinationType} URL`;
  const destinationValue =
    (isProgrammatic
      ? clickThrough.destURL ||
        clickAction.url ||
        clickAction.destURL ||
        clickAction.dest_url
      : data.testMetadata?.clickUrlDetails?.clickThrough?.destURL) || '-';
  const shouldShowProgrammaticUrl =
    isProgrammatic &&
    destinationValue !== '-' &&
    destinationType !== 'Deeplink' &&
    destinationType !== 'App';

  return (
    <div
      className="row first-row w-80 mx-auto g-0 align-items-center"
      style={{
        marginLeft: 0,
        marginRight: 0,
        paddingLeft: 8,
        paddingRight: 8,
        textAlign: 'left',
        fontFamily: 'lg-smart, sans-serif',
        fontSize: CARD_ROW_FONT_SIZE,
        fontWeight: 400,
        lineHeight: 1.25,
      }}
    >
      <div
        className="col d-flex align-items-center"
        style={{ flex: '0 0 20%', color: '#852828', minWidth: 0 }}
      >
        <SecondRowButtons
          mediaItems={mediaItems}
          data={data}
          testType={testType}
          artifactsVisible={artifactsVisible}
          assetsLoaded={assetsLoaded}
          assetsLoading={assetsLoading}
          ensureAssetsLoaded={ensureAssetsLoaded}
        />
      </div>
      <div
        className="col d-flex align-items-center"
        style={{
          flex: '0 0 80%',
          gap: '12px',
          flexWrap: 'nowrap',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flex: '0 0 150px',
            minWidth: '150px',
            color: '#000000',
            fontFamily: 'lg-smart, sans-serif',
            fontWeight: 'bold',
            fontSize: CARD_ROW_FONT_SIZE,
            whiteSpace: 'nowrap',
          }}
        >
          Destination Details
        </div>

        <FieldCell
          label="Destination Type:"
          flex="0 0 215px"
          tooltip={destinationType || undefined}
          truncate={false}
        >
          {data.testMetadata?.destination_type || '-'}
        </FieldCell>

        {isReplacedType || shouldShowProgrammaticUrl ? (
          <FieldCell
            label={`${destinationLabel}:`}
            tooltip={destinationValue !== '-' ? destinationValue : undefined}
            variant="long"
          >
            {destinationValue}
          </FieldCell>
        ) : destinationType === 'Deeplink' ? (
          <>
            <FieldCell
              label="App Group Name:"
              flex="0 0 240px"
              tooltip={appId !== '-' ? appId : undefined}
              valueMaxWidth={95}
            >
              {appId}
            </FieldCell>
            <FieldCell
              label="Content Target:"
              tooltip={contentTarget !== '-' ? contentTarget : undefined}
              variant="long"
              valueMaxWidth="420px"
              multiline={isContentTargetMultiline}
            >
              {contentTarget}
            </FieldCell>
          </>
        ) : (
          <FieldCell
            label="App Group Name:"
            flex="1 1 auto"
            tooltip={appId !== '-' ? appId : undefined}
          >
            {appId}
          </FieldCell>
        )}
      </div>
      <hr
        style={{
          marginLeft: 0,
          marginRight: 0,
          marginTop: 6,
          marginBottom: 0,
          height: '2px',
          border: 'none',
          width: '100%',
        }}
      />
    </div>
  );
}
